import { Role } from '../../models/enums/Role';
import type { Permissions } from './usePermissions';

export interface PermissionOverrides {
  canAssignAdministratorRole?: boolean;
}

const ADMIN_ROLE_ASSIGNMENT_CLAIM_KEYS = [
  'canAssignAdministratorRole',
  'CanAssignAdministratorRole',
] as const;

const matchAny = (userRoles: string[], allowed: readonly Role[]): boolean =>
  allowed.some((role) => userRoles.includes(role));

const parseBooleanClaim = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }
  }

  return undefined;
};

const readAdminAssignmentClaim = (
  source: Record<string, unknown>,
): boolean | undefined => {
  for (const claimKey of ADMIN_ROLE_ASSIGNMENT_CLAIM_KEYS) {
    const parsed = parseBooleanClaim(source[claimKey]);

    if (parsed !== undefined) {
      return parsed;
    }
  }

  const nestedSources = [source.permissions, source.capabilities];

  for (const nestedSource of nestedSources) {
    if (!nestedSource || typeof nestedSource !== 'object') {
      continue;
    }

    const parsed = readAdminAssignmentClaim(
      nestedSource as Record<string, unknown>,
    );

    if (parsed !== undefined) {
      return parsed;
    }
  }

  return undefined;
};

export const resolvePermissionOverridesFromToken = (
  token: string | null | undefined,
): PermissionOverrides => {
  if (!token) {
    return {};
  }

  try {
    const payload = token.split('.')[1];

    if (!payload) {
      return {};
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const base64 = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    const decoded = JSON.parse(atob(base64)) as Record<string, unknown>;
    const canAssignAdministratorRole = readAdminAssignmentClaim(decoded);

    return canAssignAdministratorRole === undefined
      ? {}
      : { canAssignAdministratorRole };
  } catch {
    return {};
  }
};

export const buildPermissions = (
  roles: string[] = [],
  overrides: PermissionOverrides = {},
): Permissions => {
  const has = (allowed: readonly Role[]) => matchAny(roles, allowed);
  const roleDerivedAdminAssignment = has([Role.SuperAdmin]);
  const canAssignAdministratorRole =
    overrides.canAssignAdministratorRole === undefined
      ? roleDerivedAdminAssignment
      : roleDerivedAdminAssignment && overrides.canAssignAdministratorRole;

  return {
    canViewJournals: has([
      Role.User,
      Role.ClinicAdmin,
      Role.Admin,
      Role.SuperAdmin,
    ]),
    canViewStatistics: has([
      Role.User,
      Role.ClinicAdmin,
      Role.Admin,
      Role.SuperAdmin,
    ]),
    canViewAnalytics: has([
      Role.User,
      Role.ClinicAdmin,
      Role.Admin,
      Role.SuperAdmin,
    ]),
    canViewProfile: has([
      Role.User,
      Role.ClinicAdmin,
      Role.Admin,
      Role.SuperAdmin,
    ]),
    canViewBlacklist: has([Role.ClinicAdmin, Role.Admin, Role.SuperAdmin]),
    canViewSipAccounts: has([Role.SuperAdmin]),
    canViewUsers: has([Role.Admin, Role.SuperAdmin]),
    canViewCallSettings: has([Role.Admin, Role.SuperAdmin]),
    canViewCallPools: has([Role.SuperAdmin]),
    canViewSurveyTemplates: has([Role.Admin, Role.SuperAdmin]),
    canViewSettingsMenu: has([Role.ClinicAdmin, Role.Admin, Role.SuperAdmin]),

    canUploadTasks: has([Role.ClinicAdmin, Role.Admin, Role.SuperAdmin]),
    canManageSurveyTemplates: has([Role.Admin, Role.SuperAdmin]),
    canManageBlacklist: has([Role.ClinicAdmin, Role.Admin, Role.SuperAdmin]),
    canAssignAdministratorRole,
  };
};
